/**
 * Responsibility: show a small text readout of what the world thinks is happening.
 *
 * A development aid, not part of the experience. It exists because biome blending is close to
 * impossible to judge by eye — "is this a transition or just a small mountain range" is not a
 * question the view answers, but two numbers answer it instantly.
 *
 * Owns its own DOM element and its own toggle key. It reads state and writes text; it never
 * touches the simulation. Task 11's HUD may absorb whatever turns out to be worth keeping.
 */

import { DEBUG, INPUT } from '../config.js';

/** Percentage, for turning a 0..1 weight into something readable at a glance. */
const PERCENT = 100;

/**
 * Create the overlay and attach it to the document.
 *
 * @param {object} [options]
 * @param {HTMLElement} [options.parent] - Defaults to `document.body`.
 * @returns {{update: Function, isVisible: () => boolean, dispose: () => void}}
 */
export function createDebugOverlay({ parent = document.body, visible: initiallyVisible } = {}) {
  const element = document.createElement('pre');

  /*
    Styled in code rather than in index.html. This is scaffolding, and keeping it entirely in
    one file means deleting the feature is deleting the file.
  */
  Object.assign(element.style, {
    position: 'fixed',
    top: '12px',
    left: '14px',
    margin: '0',
    padding: '0',
    font: '12px/1.5 ui-monospace, "Cascadia Code", Consolas, monospace',
    // Dim on purpose: it should be readable when looked at and ignorable when not.
    color: 'rgba(150, 220, 255, 0.55)',
    textShadow: '0 0 6px rgba(0, 0, 0, 0.9)',
    pointerEvents: 'none',
    whiteSpace: 'pre',
    zIndex: '10',
  });

  /*
    The caller may override the configured default, because the settings store remembers whether the
    player left the overlay on. Config remains the authority on the default itself.
  */
  let visible = initiallyVisible === undefined ? DEBUG.SHOW_OVERLAY : Boolean(initiallyVisible);
  element.style.display = visible ? 'block' : 'none';
  parent.appendChild(element);

  function handleKeyDown(event) {
    if (event.code !== DEBUG.TOGGLE_KEY && event.key !== DEBUG.TOGGLE_KEY) return;

    // F-keys otherwise open browser help or developer tools.
    event.preventDefault();
    visible = !visible;
    element.style.display = visible ? 'block' : 'none';
  }

  window.addEventListener('keydown', handleKeyDown);

  /** Seconds until the next refresh is due. Starts at 0 so the first frame fills it in. */
  let untilNextRefresh = 0;

  return {
    /**
     * Refresh the readout, at most every `DEBUG.OVERLAY_INTERVAL` seconds.
     *
     * @param {number} dt - Seconds since the last call.
     * @param {object} info
     * @param {{x: number, y: number, z: number}} info.aircraft - Aircraft position.
     * @param {number} info.groundElevation - Drawn surface height beneath the aircraft.
     * @param {Array<{id: string, weight: number}>} info.weights - Biome weights here.
     * @param {number} info.tileCount
     * @param {number} info.segmentCount
     * @param {{frames: number, averageMs: number, worstMs: number, fps: number}} [info.frame]
     *   Frame timing summary. The *worst* figure is the one to watch: a tile build shows up as a
     *   rare spike rather than a raised average.
     * @param {object} [info.profile] - From `frameProfiler.snapshot()`. Where the frame time goes.
     * @param {object} [info.render] - From the renderer's `getRenderInfo()`. How much it is being asked to fill.
     * @param {{worker: boolean, pending: number}} [info.stream] - Whether tiles are being built off-thread.
     */
    update(dt, info) {
      if (!visible) return;

      untilNextRefresh -= dt;
      if (untilNextRefresh > 0) return;
      untilNextRefresh = DEBUG.OVERLAY_INTERVAL;

      /*
        Sorted strongest first and shown as whole percentages. Ordering by weight rather than
        by name means the dominant biome is always on the first line, which is what makes a
        transition legible while flying.
      */
      const biomeLines = [...info.weights]
        .sort((a, b) => b.weight - a.weight)
        .map(({ id, weight }) => `  ${id.padEnd(10)} ${(weight * PERCENT).toFixed(0).padStart(3)}%`)
        .join('\n');

      const frameLine = info.frame
        ? `frame ${info.frame.averageMs.toFixed(1)} ms avg  ${info.frame.worstMs.toFixed(1)} ms worst  ${info.frame.fps.toFixed(0)} fps`
        : null;

      /*
        Where the frame actually goes, on screen rather than in the console.

        **The distinction the frame line above cannot make.** An average of 25 ms says the same thing whether
        our own JavaScript is spending it or the GPU is, and those need opposite fixes. Two lines here — the
        phase split, and how much the GPU is being asked to fill — settle it from a single screenshot, which
        matters because opening devtools to read a console changes the very thing being measured.
      */
      const profileLines = [];

      if (info.profile) {
        profileLines.push(
          /*
            Our work against the frame it sat in. **The whole diagnosis is the gap between these two.** Work
            far below the interval means the time is going somewhere our JavaScript is not running — the GPU,
            the compositor, or collection between frames — and doing less work cannot recover any of it.
          */
          `work  ${info.profile.averageWorkMs.toFixed(1)} of ${info.profile.averageIntervalMs.toFixed(1)} ms` +
            `   ${info.profile.averageIntervalMs - info.profile.averageWorkMs > info.profile.averageWorkMs ? 'most of the frame is outside our JS' : 'our own work dominates'}`,
          `      ${info.profile.phases
            .map((phase) => `${phase.name} ${phase.averageMs.toFixed(1)}`)
            .join('  ')}`,
          `worst ${info.profile.worstMs.toFixed(1)} ms  = ${info.profile.worstPhases
            .map((phase) => `${phase.name} ${phase.ms.toFixed(1)}`)
            .join('  ')}`,
          `lost  ${info.profile.lostFrames} frames, ${info.profile.lostToOurWork} to our own work   ` +
            `builds ${info.profile.builds}, worst ${info.profile.worstBuildMs.toFixed(1)} ms`,
        );
      }

      if (info.render) {
        profileLines.push(
          `gpu   ${info.render.width}x${info.render.height} @${info.render.pixelRatio}  ` +
            `${info.render.drawCalls} calls  bloom ${info.render.bloom ? 'on' : 'off'}`,
        );
      }

      /*
        Whether tiles are being generated off-thread.

        **Worth a line of its own, because the fallback is silent by design.** If a worker cannot run the world
        still loads, on this thread, exactly as it used to — which also means the hitches come back. Without
        this, that would look like the fix having failed rather than the worker having failed.
      */
      if (info.stream) {
        profileLines.push(
          `tiles ${info.stream.worker ? 'built on a worker thread' : 'built on THIS thread (worker unavailable)'}` +
            `   ${info.stream.pending} in flight`,
        );
      }

      element.textContent = [
        frameLine,
        ...profileLines,
        `pos   ${info.aircraft.x.toFixed(0)}, ${info.aircraft.z.toFixed(0)}`,
        `alt   ${info.aircraft.y.toFixed(0)}   ground ${info.groundElevation.toFixed(0)}   clear ${(info.aircraft.y - info.groundElevation).toFixed(0)}`,
        `tiles ${info.tileCount}   segments ${info.segmentCount.toLocaleString()}`,
        `craft ${info.aircraftLabel ?? '-'}`,
        'biome',
        biomeLines,
        `[${DEBUG.TOGGLE_KEY}] hide   [${INPUT.CYCLE_AIRCRAFT_KEY.replace('Key', '')}] aircraft`,
      ]
        .filter((line) => line !== null)
        .join('\n');
    },

    /** Whether the readout is currently shown. */
    isVisible() {
      return visible;
    },

    /**
     * Show or hide the readout.
     *
     * The toggle key still works; this exists so the settings menu can drive the same state rather
     * than keeping a competing copy of it.
     *
     * @param {boolean} next
     */
    setVisible(next) {
      visible = Boolean(next);
      element.style.display = visible ? 'block' : 'none';
    },

    /** Remove the element and detach the toggle listener. */
    dispose() {
      window.removeEventListener('keydown', handleKeyDown);
      element.remove();
    },
  };
}
